import React, { useState, useEffect } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);
const MotionText = motion(Text);

const PageLoader = ({ onLoadComplete }) => {
  const [progress, setProgress] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // Preload hero image with timeout fallback
    const img = new Image();
    img.src = '/hero-bg.jpg';
    
    const imageTimeout = setTimeout(() => {
      // If image takes too long, proceed anyway
      setImageLoaded(true);
    }, 3000); // 3 second timeout
    
    img.onload = () => {
      clearTimeout(imageTimeout);
      setImageLoaded(true);
    };

    img.onerror = () => {
      clearTimeout(imageTimeout);
      // If image fails to load, proceed anyway
      setImageLoaded(true);
    };

    // Simulate loading progress - faster on mobile
    const isMobile = window.innerWidth < 768;
    const progressSpeed = isMobile ? 80 : 100;
    const progressIncrement = isMobile ? 12 : 10;
    
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + progressIncrement;
      });
    }, progressSpeed);

    return () => {
      clearInterval(interval);
      clearTimeout(imageTimeout);
    };
  }, []);

  useEffect(() => {
    // When both image is loaded and progress reaches 100, wait a bit then complete
    if (imageLoaded && progress >= 100) {
      const isMobile = window.innerWidth < 768;
      const delay = isMobile ? 300 : 400; // Faster transition on mobile
      
      const timer = setTimeout(() => {
        onLoadComplete();
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [imageLoaded, progress, onLoadComplete]);

  return (
    <MotionBox
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="black"
      zIndex={9999}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      initial={{ opacity: 1 }}
      exit={{ 
        opacity: 0,
        transition: { duration: 0.8, ease: "easeInOut" }
      }}
    >
      {/* AGNIR Text with Blinking Effect */}
      <MotionText
        fontSize={{ base: "6xl", md: "8xl", lg: "9xl" }}
        fontWeight="800"
        fontFamily="'Cormorant', serif"
        letterSpacing="wider"
        color="white"
        animate={{
          opacity: [0.3, 1, 0.3],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        textShadow="0 0 30px rgba(255,255,255,0.5)"
      >
        AGNIR
      </MotionText>

      {/* Subtle Loading Bar */}
      <Box
        mt={8}
        width={{ base: "60%", md: "400px" }}
        height="2px"
        bg="rgba(255,255,255,0.1)"
        borderRadius="full"
        overflow="hidden"
      >
        <motion.div
          style={{
            width: "100%",
            height: "100%",
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)",
            borderRadius: "full",
          }}
          animate={{
            x: ["-100%", "100%"],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </Box>

      {/* Loading Text */}
      <MotionText
        mt={4}
        fontSize={{ base: "xs", md: "sm" }}
        color="rgba(255,255,255,0.6)"
        fontFamily="'Zain', sans-serif"
        letterSpacing="wider"
        textTransform="uppercase"
        animate={{
          opacity: [0.5, 1, 0.5],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        Loading Experience...
      </MotionText>

      {/* Decorative Corner Elements */}
      <Box position="absolute" top="2rem" left="2rem" opacity={0.3}>
        <motion.div
          animate={{
            rotate: [0, 360],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <Box
            w={{ base: "40px", md: "60px" }}
            h={{ base: "40px", md: "60px" }}
            border="1px solid white"
            borderRadius="full"
          />
        </motion.div>
      </Box>

      <Box position="absolute" bottom="2rem" right="2rem" opacity={0.3}>
        <motion.div
          animate={{
            rotate: [360, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <Box
            w={{ base: "30px", md: "40px" }}
            h={{ base: "30px", md: "40px" }}
            border="1px solid white"
            transform="rotate(45deg)"
          />
        </motion.div>
      </Box>
    </MotionBox>
  );
};

export default PageLoader;
